import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Grid,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NoteIcon from "@mui/icons-material/Note";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import { ContactPreferenceType } from "../../../interfaces/DTO/contactPreferences";
import { MainInsuranceBranch } from "../../../interfaces/DTO/insurance_data/InsuranceData";
import { formatMainInsuranceBranch, colorMainInsuranceBranch } from "../../../utils/insuranceUtil";
import {
  useContactPreferencesByContactId,
  usePartialUpdateContactPreferences,
  useCreateContactPreferences,
} from "../../../hooks/useContactPreferences";
import CurrencyTextField from "../../../shared/UIParts/InputFields/CurrencyTextField";
import PercentageTextField from "../../../shared/UIParts/InputFields/PercentageTextField";
import { useQueryClient } from "@tanstack/react-query";

interface InsuranceClientQuestionnaireProps {
  contactId: number;
  reportedPeriod?: string;
  defaultExpanded?: boolean;
}

// הגדרת סוגי שדות
type FieldType = "text" | "number" | "currency" | "yesno" | "percentage";

// הגדרת שאלה בודדת
interface QuestionConfig {
  key: ContactPreferenceType;
  label: string;
  fieldType: FieldType;
  conditionalOn?: {
    key: ContactPreferenceType;
    value: string;
  };
}

// הגדרת קטגוריה
interface CategoryConfig {
  insuranceBranch: MainInsuranceBranch;
  questions: QuestionConfig[];
}

// קונפיגורציה של כל הקטגוריות
const categoriesConfig: CategoryConfig[] = [
  {
    insuranceBranch: MainInsuranceBranch.LOSS_OF_WORK_CAPACITY,
    questions: [
      {
        key: ContactPreferenceType.HAS_SPECIFIC_PROFESSION,
        label: "האם יש מקצוע ספציפי שכדאי לבטח?",
        fieldType: "yesno",
      },
      {
        key: ContactPreferenceType.PROFESSION_TO_INSURE,
        label: "המקצוע לביטוח",
        fieldType: "text",
      },
      {
        key: ContactPreferenceType.GROSS_SALARY,
        label: "שכר ברוטו",
        fieldType: "currency",
      },
      {
        key: ContactPreferenceType.DISABILITY_PERCENTAGE,
        label: "ביטוח רצוי לאובדן כושר עבודה",
        fieldType: "percentage",
      },
      {
        key: ContactPreferenceType.HAS_PROGRESSIVE_DISEASE,
        label: "האם יש מחלה עם תהליך מתפתח?",
        fieldType: "yesno",
      },
    ],
  },
  {
    insuranceBranch: MainInsuranceBranch.LIFE,
    questions: [
      {
        key: ContactPreferenceType.SIGN_LONGEVITY_AT_BANK,
        label: "לחתום על אריכות ימים בבנק?",
        fieldType: "yesno",
      },
    ],
  },
  {
    insuranceBranch: MainInsuranceBranch.HEALTH,
    questions: [
      {
        key: ContactPreferenceType.CHILDREN_HEALTH_INSURANCE,
        label: "האם יש לילדים ביטוח בריאות?",
        fieldType: "yesno",
      },
    ],
  },
];

const InsuranceClientQuestionnaire: React.FC<InsuranceClientQuestionnaireProps> = ({
  contactId,
  reportedPeriod,
  defaultExpanded = false,
}) => {
  // State דינמי לכל התשובות
  const [answers, setAnswers] = useState<Record<string, string | number>>({});

  // State for notes
  const [openNoteFields, setOpenNoteFields] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  // שימוש ב-Hooks
  const queryClient = useQueryClient();
  const { data: preferences, isLoading } = useContactPreferencesByContactId(contactId);
  const partialUpdateMutation = usePartialUpdateContactPreferences();
  const createMutation = useCreateContactPreferences();

  // טעינת נתונים קיימים
  useEffect(() => {
    if (preferences) {
      const answerMap: Record<string, string | number> = {};
      const notesMap: Record<string, string> = {};

      preferences.forEach((pref: any) => {
        // שמירת הערות אם יש
        if (pref.notes) {
          notesMap[pref.preference_type] = pref.notes;
        }

        // שמירת תשובה
        if (pref.data?.answer !== undefined) {
          answerMap[pref.preference_type] = pref.data.answer;
        }
      });

      setAnswers(answerMap);
      setNotes(notesMap);
    }
  }, [preferences]);

  // פונקציה לעדכון אוטומטי של שדה - create או partialUpdate
  const handleFieldUpdate = useCallback(
    async (preferenceType: ContactPreferenceType, answer: string | number) => {
      if (!answer && answer !== 0) return;

      try {
        const existing = preferences?.find((p) => p.preference_type === preferenceType);

        // שימוש ב-reportedPeriod מה-props, או תחילת החודש הנוכחי אם לא הועבר
        const periodToUse = reportedPeriod || new Date(new Date().getFullYear(), new Date().getMonth(), 1)
          .toISOString()
          .split('T')[0];

        if (existing) {
          // עדכון רשומה קיימת
          await partialUpdateMutation.mutateAsync({
            id: existing.id,
            fields: {
              data: { answer: String(answer) },
              reported_period: periodToUse,
            },
          });
        } else {
          // יצירת רשומה חדשה
          await createMutation.mutateAsync({
            contact_id: contactId,
            preference_type: preferenceType,
            data: { answer: String(answer) },
            is_family: false,
            reported_period: periodToUse,
          });
        }

        // רענון ה-cache
        await queryClient.invalidateQueries({
          queryKey: ["contactPreferences", "byContact", contactId],
        });
      } catch (error) {
        console.error("Error updating field:", error);
      }
    },
    [preferences, partialUpdateMutation, createMutation, contactId, reportedPeriod, queryClient]
  );

  // פונקציה לעדכון הערה
  const handleNoteUpdate = useCallback(
    async (preferenceType: ContactPreferenceType, note: string) => {
      try {
        const existing = preferences?.find((p) => p.preference_type === preferenceType);

        // שימוש ב-reportedPeriod מה-props, או תחילת החודש הנוכחי אם לא הועבר
        const periodToUse = reportedPeriod || new Date(new Date().getFullYear(), new Date().getMonth(), 1)
          .toISOString()
          .split('T')[0];

        if (existing) {
          // עדכון הערה ברשומה קיימת
          await partialUpdateMutation.mutateAsync({
            id: existing.id,
            fields: {
              notes: note,
              reported_period: periodToUse,
            },
          });
        } else {
          // יצירת רשומה חדשה עם הערה (ללא תשובה)
          await createMutation.mutateAsync({
            contact_id: contactId,
            preference_type: preferenceType,
            data: { answer: "" },
            notes: note,
            is_family: false,
            reported_period: periodToUse,
          });
        }

        // רענון ה-cache
        await queryClient.invalidateQueries({
          queryKey: ["contactPreferences", "byContact", contactId],
        });
      } catch (error) {
        console.error("Error updating note:", error);
      }
    },
    [preferences, partialUpdateMutation, createMutation, contactId, reportedPeriod, queryClient]
  );

  const toggleNoteField = (key: string) => {
    setOpenNoteFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Helper component for note button
  const NoteButton: React.FC<{ preferenceType: ContactPreferenceType }> = ({ preferenceType }) => (
    <Box
      onClick={() => toggleNoteField(preferenceType)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        cursor: "pointer",
        color: notes[preferenceType] ? "#1976d2" : "#757575",
        "&:hover": {
          color: "#1976d2"
        },
        mt: 1
      }}
    >
      {notes[preferenceType] ? (
        <NoteIcon fontSize="small" />
      ) : (
        <NoteAddIcon fontSize="small" sx={{ color: "inherit" }} />
      )}
      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
        {notes[preferenceType] ? "ערוך הערה" : "הוסף הערה"}
      </Typography>
    </Box>
  );

  // Helper component for note display and edit
  const NoteField: React.FC<{ preferenceType: ContactPreferenceType }> = ({ preferenceType }) => (
    <>
      {notes[preferenceType] && !openNoteFields[preferenceType] && (
        <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block", textAlign: "right", direction: "rtl" }}>
          הערה: {notes[preferenceType]}
        </Typography>
      )}
      <Collapse in={openNoteFields[preferenceType]}>
        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          label="הערות"
          value={notes[preferenceType] || ""}
          onChange={(e) => {
            const value = e.target.value;
            setNotes({ ...notes, [preferenceType]: value });
          }}
          onBlur={(e) => handleNoteUpdate(preferenceType, e.target.value)}
          sx={{ mt: 1, direction: "rtl" }}
          slotProps={{
            input: {
              style: { textAlign: "right", direction: "rtl" }
            }
          }}
        />
      </Collapse>
    </>
  );

  // Helper component to render a question field based on config
  const renderQuestionField = (question: QuestionConfig) => {
    const value = answers[question.key] || "";

    const handleChange = (newValue: string | number) => {
      setAnswers((prev) => ({ ...prev, [question.key]: newValue }));
    };

    const handleBlur = (newValue: string | number) => {
      handleFieldUpdate(question.key, newValue);
    };

    switch (question.fieldType) {
      case "yesno":
        return (
          <RadioGroup
            row
            value={value}
            onChange={(e) => {
              const newValue = e.target.value;
              handleChange(newValue);
              handleFieldUpdate(question.key, newValue); // שמירה מיידית לרדיו
            }}
            // sx={{ display: "flex", justifyContent: "flex-end", direction: "rtl" }}
          >
            <FormControlLabel value="yes" control={<Radio size="small" />} label="כן" />
            <FormControlLabel value="no" control={<Radio size="small" />} label="לא" />
          </RadioGroup>
        );

      case "text":
        return (
          <TextField
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={(e) => handleBlur(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            slotProps={{
              input: {
                style: { textAlign: "right", direction: "rtl" }
              }
            }}
            sx={{ direction: "rtl" }}
          />
        );

      case "number":
        return (
          <TextField
            value={value}
            onChange={(e) => handleChange(Number(e.target.value))}
            onBlur={(e) => handleBlur(Number(e.target.value))}
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            slotProps={{
              input: {
                style: { textAlign: "right" }
              },
              htmlInput: {
                min: 0,
                max: 100
              }
            }}
            sx={{ direction: "rtl" }}
          />
        );

      case "currency":
        return (
          <CurrencyTextField
            value={Number(value) || 0}
            onChange={(newValue) => handleChange(newValue ?? 0)}
            onBlur={(newValue) => handleFieldUpdate(question.key, newValue ?? 0)}
            variant="outlined"
            size="small"
            fullWidth
            sx={{ direction: "rtl" }}
          />
        );

      case "percentage":
        return (
          <PercentageTextField
            value={Number(value) || 0}
            onChange={(newValue) => handleChange(newValue)}
            onBlur={(newValue) => handleFieldUpdate(question.key, newValue)}
            variant="outlined"
            size="small"
            sx={{ direction: "rtl" }}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ mb: 3, direction: "rtl", display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Accordion
        defaultExpanded={defaultExpanded}
        sx={{
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
          borderRadius: "8px !important",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          "&:before": {
            display: "none"
          },
          "&.Mui-expanded": {
            margin: 0
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "#f8f9fa",
            borderRadius: "8px 8px 0 0",
            minHeight: "56px",
            "& .MuiAccordionSummary-content": {
              margin: "12px 0",
           
              justifyContent: "space-between",
              alignItems: "center"
            },
            "& .MuiAccordionSummary-expandIconWrapper": {
              marginRight: "12px",
              marginLeft: 0
            },
            "&:hover": {
              backgroundColor: "#f0f1f3"
            }
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "1.1rem",
              color: "#1976d2",
              paddingRight: 2
            }}
          >
            העדפות לקוח לביטוח
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 3, backgroundColor: "#ffffff", direction: "rtl" }}>
          <Stack spacing={3}>
            {categoriesConfig.map((category, categoryIndex) => (
              <Box key={categoryIndex}>
                {/* Chip של הקטגוריה */}
                <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-start" }}>
                  <Chip
                    label={formatMainInsuranceBranch(category.insuranceBranch)}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: colorMainInsuranceBranch(category.insuranceBranch),
                      color: "#ffffff",
                    }}
                  />
                </Box>

                {/* Grid של השאלות */}
                <Grid container spacing={2}>
                  {category.questions.map((question) => {
                    // בדיקה אם השאלה תלויה בתנאי
                    if (question.conditionalOn) {
                      const conditionMet = answers[question.conditionalOn.key] === question.conditionalOn.value;
                      if (!conditionMet) return null;
                    }

                    return (
                      <Grid item xs={12} md={6} key={question.key}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: "#fafafa",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            height: "100%"
                          }}
                        >
                          <Typography sx={{ fontWeight: 500, color: "#424242", textAlign: "right", mb: 1.5 }}>
                            {question.label}
                          </Typography>
                          {renderQuestionField(question)}
                          <NoteButton preferenceType={question.key} />
                          <NoteField preferenceType={question.key} />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default InsuranceClientQuestionnaire;
